'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { BaseLayoutConfig, BaseLayoutSection, DayOfWeek } from '@/features/base/types/base-layout'
import { sanitizeHtmlContent } from '@/features/base/utils/sanitize'
import { addConfigurableHubTab, ensurePermanentArTab, resolveBaseLayoutConfig } from '@/features/base/utils/layout'
import { BASE_AR_TAB_ID, BASE_AR_TAB_LABEL } from '@/features/base/constants/base-tab-links'
import successToast from '@/shared/lib/toast/success.toast'
import errorToast from '@/shared/lib/toast/error.toast'
import { RichTextEditor } from '../rich-text-editor'
import { unwrapBaseRegionResponse } from '@/features/base/types/region-response'
import { buildBaseLayoutContext } from '@/features/base/utils/context'
import { hydrateLayoutWithContext } from '@/features/base/utils/layout-hydration'
import StickySaveBar from '@/shared/components/sticky-save-bar'
import { authFetch, authFetchJson } from '@/shared/lib/auth-fetch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'

type BaseLayoutEditorProps = {
  regionId: number
  initialLayout: BaseLayoutConfig
  onLayoutReload: () => Promise<void>
}

const DAY_LABELS: Record<DayOfWeek, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
}

const DAY_ORDER: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const DAILY_MESSAGE_DEFAULT = DAY_ORDER.reduce<Record<DayOfWeek, string>>((acc, day) => {
  acc[day] = ''
  return acc
}, {
  sunday: '',
  monday: '',
  tuesday: '',
  wednesday: '',
  thursday: '',
  friday: '',
  saturday: '',
})

const DEFAULT_SECTION_TITLE: Record<string, string> = {
  textBox: 'Text Section',
  dailyMessage: 'Daily Messages',
  basePhotos: 'Photos',
  missionStories: 'Mission Stories',
  prayerRequests: 'Prayer Requests',
  partnerGiveList: 'Partner Give List',
}

function generateId(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function sanitizeLayoutForSave(config: BaseLayoutConfig): BaseLayoutConfig {
  const withPermanentArTab = ensurePermanentArTab(config)
  return {
    version: withPermanentArTab.version,
    tabs: withPermanentArTab.tabs.map((tab) => ({
      ...tab,
      sections: tab.sections.map((section) => {
        if (section.type === 'textBox') {
          const isRotating = section.options?.['isRotating'] === true
          if (isRotating) {
            // Handle rotating textBox
            const optionDays = (section.options?.['days'] as Record<DayOfWeek, string | undefined>) ?? {}
            const sanitizedDays = DAY_ORDER.reduce<Record<DayOfWeek, string>>((acc, day) => {
              acc[day] = sanitizeHtmlContent(optionDays[day] ?? '') ?? ''
              return acc
            }, { ...DAILY_MESSAGE_DEFAULT })

            return {
              ...section,
              content: undefined,
              options: {
                ...section.options,
                isRotating: true,
                days: sanitizedDays,
              },
            }
          } else {
            // Handle regular textBox
            const safeContent = sanitizeHtmlContent(section.content ?? '') ?? ''
            return {
              ...section,
              content: safeContent,
            }
          }
        }

        if (section.type === 'dailyMessage') {
          const optionDays = (section.options?.['days'] as Record<DayOfWeek, string | undefined>) ?? {}
          const sanitizedDays = DAY_ORDER.reduce<Record<DayOfWeek, string>>((acc, day) => {
            acc[day] = sanitizeHtmlContent(optionDays[day] ?? '') ?? ''
            return acc
          }, { ...DAILY_MESSAGE_DEFAULT })

          return {
            ...section,
            options: {
              ...section.options,
              days: sanitizedDays,
            },
          }
        }

        const safeContent = sanitizeHtmlContent(section.content ?? '')
        return {
          ...section,
          content: safeContent ?? section.content,
        }
      }),
    })),
  }
}

function cloneLayout(layout: BaseLayoutConfig): BaseLayoutConfig {
  return JSON.parse(JSON.stringify(layout))
}

export function BaseLayoutEditor({ regionId, initialLayout, onLayoutReload }: BaseLayoutEditorProps) {
  const [layoutDraft, setLayoutDraft] = useState<BaseLayoutConfig>(cloneLayout(ensurePermanentArTab(initialLayout)))
  const [isSaving, setIsSaving] = useState(false)
  const [showSuccessBar, setShowSuccessBar] = useState(false)
  const [reassignModal, setReassignModal] = useState<{
    pending: Array<{ from: string; to: string; count: number }>
  } | null>(null)
  const initialSerializedRef = useRef(JSON.stringify(initialLayout))

  useEffect(() => {
    const withAr = ensurePermanentArTab(initialLayout)
    setLayoutDraft(cloneLayout(withAr))
    initialSerializedRef.current = JSON.stringify(withAr)
  }, [initialLayout])

  const isDirty = useMemo(() => {
    return JSON.stringify(layoutDraft) !== initialSerializedRef.current
  }, [layoutDraft])

  // Hide success bar when form becomes dirty again
  useEffect(() => {
    if (isDirty && showSuccessBar) {
      setShowSuccessBar(false)
    }
  }, [isDirty, showSuccessBar])

  const handleTabLabelChange = (index: number, value: string) => {
    setLayoutDraft((prev) => {
      if (prev.tabs[index]?.id === BASE_AR_TAB_ID) return prev
      const nextTabs = [...prev.tabs]
      nextTabs[index] = { ...nextTabs[index], label: value }
      return { ...prev, tabs: nextTabs }
    })
  }

  const handleAddTab = () => {
    setLayoutDraft((prev) =>
      addConfigurableHubTab(prev, {
        id: generateId('tab'),
        label: `New Tab ${prev.tabs.filter((tab) => tab.id !== BASE_AR_TAB_ID).length + 1}`,
        sections: [],
      }),
    )
  }

  const handleRemoveTab = (index: number) => {
    setLayoutDraft((prev) => {
      if (prev.tabs[index]?.id === BASE_AR_TAB_ID) return prev
      return ensurePermanentArTab({
        ...prev,
        tabs: prev.tabs.filter((_, idx) => idx !== index),
      })
    })
  }

  const handleMoveTab = (index: number, direction: number) => {
    setLayoutDraft((prev) => {
      const nextTabs = [...prev.tabs]
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= nextTabs.length) {
        return prev
      }
      if (nextTabs[index]?.id === BASE_AR_TAB_ID || nextTabs[targetIndex]?.id === BASE_AR_TAB_ID) {
        return prev
      }
      const [removed] = nextTabs.splice(index, 1)
      nextTabs.splice(targetIndex, 0, removed)
      return ensurePermanentArTab({ ...prev, tabs: nextTabs })
    })
  }

  const updateSection = (tabIndex: number, sectionIndex: number, updater: (section: BaseLayoutSection) => BaseLayoutSection) => {
    setLayoutDraft((prev) => {
      const nextTabs = [...prev.tabs]
      const tab = nextTabs[tabIndex]
      const nextSections = [...tab.sections]
      nextSections[sectionIndex] = updater(nextSections[sectionIndex])
      nextTabs[tabIndex] = { ...tab, sections: nextSections }
      return { ...prev, tabs: nextTabs }
    })
  }

  const handleSectionOptionChange = (
    tabIndex: number,
    sectionIndex: number,
    optionKey: string,
    value: unknown,
  ) => {
    updateSection(tabIndex, sectionIndex, (section) => {
      return {
        ...section,
        options: {
          ...section.options,
          [optionKey]: value,
        },
      }
    })
  }

  const handleAddSection = (tabIndex: number, type: BaseLayoutSection['type']) => {
    setLayoutDraft((prev) => {
      const nextTabs = [...prev.tabs]
      const tab = nextTabs[tabIndex]
      const newSection: BaseLayoutSection = {
        id: generateId('section'),
        type,
        title: DEFAULT_SECTION_TITLE[type] ?? 'Section',
        content: type === 'textBox' ? '' : undefined,
        options:
          type === 'dailyMessage'
            ? { days: { ...DAILY_MESSAGE_DEFAULT } }
            : {},
      }
      nextTabs[tabIndex] = { ...tab, sections: [...tab.sections, newSection] }
      return { ...prev, tabs: nextTabs }
    })
  }

  const handleRemoveSection = (tabIndex: number, sectionIndex: number) => {
    setLayoutDraft((prev) => {
      const nextTabs = [...prev.tabs]
      const tab = nextTabs[tabIndex]
      const nextSections = tab.sections.filter((_, idx) => idx !== sectionIndex)
      nextTabs[tabIndex] = { ...tab, sections: nextSections }
      return { ...prev, tabs: nextTabs }
    })
  }

  const handleMoveSection = (tabIndex: number, sectionIndex: number, direction: number) => {
    setLayoutDraft((prev) => {
      const nextTabs = [...prev.tabs]
      const tab = nextTabs[tabIndex]
      const targetIndex = sectionIndex + direction
      if (targetIndex < 0 || targetIndex >= tab.sections.length) {
        return prev
      }
      const nextSections = [...tab.sections]
      const [removed] = nextSections.splice(sectionIndex, 1)
      nextSections.splice(targetIndex, 0, removed)
      nextTabs[tabIndex] = { ...tab, sections: nextSections }
      return { ...prev, tabs: nextTabs }
    })
  }

  const handleSectionTitleChange = (tabIndex: number, sectionIndex: number, value: string) => {
    updateSection(tabIndex, sectionIndex, (section) => ({
      ...section,
      title: value,
    }))
  }

  const handleSectionContentChange = (tabIndex: number, sectionIndex: number, value: string) => {
    updateSection(tabIndex, sectionIndex, (section) => ({
      ...section,
      content: value,
    }))
  }

  const handleDailyMessageChange = (tabIndex: number, sectionIndex: number, day: DayOfWeek, value: string) => {
    updateSection(tabIndex, sectionIndex, (section) => {
      const optionDays = (section.options?.['days'] as Record<DayOfWeek, string>) ?? { ...DAILY_MESSAGE_DEFAULT }
      return {
        ...section,
        options: {
          ...section.options,
          days: {
            ...optionDays,
            [day]: value,
          },
        },
      }
    })
  }

  const handleRotatingTextBoxChange = (tabIndex: number, sectionIndex: number, day: DayOfWeek, value: string) => {
    updateSection(tabIndex, sectionIndex, (section) => {
      const optionDays = (section.options?.['days'] as Record<DayOfWeek, string>) ?? { ...DAILY_MESSAGE_DEFAULT }
      return {
        ...section,
        options: {
          ...section.options,
          days: {
            ...optionDays,
            [day]: value,
          },
        },
      }
    })
  }

  const handleToggleRotatingTextBox = (tabIndex: number, sectionIndex: number, enabled: boolean) => {
    updateSection(tabIndex, sectionIndex, (section) => {
      if (enabled) {
        // Enable rotation: initialize days with current content or empty
        const currentContent = section.content ?? ''
        const initialDays = DAY_ORDER.reduce<Record<DayOfWeek, string>>((acc, day) => {
          acc[day] = currentContent
          return acc
        }, { ...DAILY_MESSAGE_DEFAULT })
        
        return {
          ...section,
          content: undefined, // Clear single content when enabling rotation
          options: {
            ...section.options,
            isRotating: true,
            days: initialDays,
          },
        }
      } else {
        // Disable rotation: use first non-empty day content or empty string
        const optionDays = (section.options?.['days'] as Record<DayOfWeek, string>) ?? { ...DAILY_MESSAGE_DEFAULT }
        const firstDayWithContent = DAY_ORDER.find(day => optionDays[day] && optionDays[day].trim() !== '')
        const firstContent = firstDayWithContent ? optionDays[firstDayWithContent] : ''
        
        return {
          ...section,
          content: firstContent,
          options: {
            ...section.options,
            isRotating: false,
            days: undefined,
          },
        }
      }
    })
  }

  const handleReset = () => {
    setLayoutDraft(cloneLayout(JSON.parse(initialSerializedRef.current)))
  }

  const performSave = async (sanitized: BaseLayoutConfig) => {
    const response = await authFetch(`/regions/${regionId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ baseLayoutConfig: sanitized }),
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null)
      throw new Error(errorBody?.error ?? 'Failed to save base layout')
    }

    const payload = await response.json()
    const region = unwrapBaseRegionResponse(payload)

    if (region) {
      const context = buildBaseLayoutContext(region)
      const hydrated = hydrateLayoutWithContext(ensurePermanentArTab(resolveBaseLayoutConfig(region.baseLayoutConfig)), context)
      setLayoutDraft(cloneLayout(hydrated))
      initialSerializedRef.current = JSON.stringify(hydrated)
    } else {
      setLayoutDraft(cloneLayout(sanitized))
      initialSerializedRef.current = JSON.stringify(sanitized)
    }

    await onLayoutReload()
    successToast('Base page layout saved successfully')
    setShowSuccessBar(true)
  }

  const handleSave = async () => {
    if (isSaving) return

    const sanitized = sanitizeLayoutForSave(layoutDraft)

    setIsSaving(true)
    try {
      // Check for basePhotos section title changes that would orphan photos
      let initialParsed: BaseLayoutConfig
      try {
        initialParsed = JSON.parse(initialSerializedRef.current) as BaseLayoutConfig
      } catch {
        initialParsed = { version: 1, tabs: [] }
      }

      const pendingReassigns: Array<{ from: string; to: string; count: number }> = []

      const photosRes = await authFetchJson<any>(`/base-photos/region/${regionId}`)
      if (!photosRes.success) {
        throw new Error(photosRes.message || 'Failed to check base photos')
      }
      const photosPayload = photosRes.data
      const photosList: Array<{ componentName?: string | null }> = Array.isArray(photosPayload)
        ? photosPayload
        : Array.isArray(photosPayload?.items)
          ? photosPayload.items
          : []

      for (const tab of sanitized.tabs) {
        for (const section of tab.sections) {
          if (section.type !== 'basePhotos') continue

          const oldSection = initialParsed.tabs
            .flatMap((t) => t.sections)
            .find((s) => s.id === section.id)
          const oldTitle = oldSection?.title?.trim() || 'Base Photos'
          const newTitle = section.title?.trim() || 'Base Photos'

          if (oldTitle === newTitle) continue

          const count = photosList.filter((p) => (p.componentName ?? 'Base Photos') === oldTitle).length
          if (count > 0) {
            pendingReassigns.push({ from: oldTitle, to: newTitle, count })
          }
        }
      }

      if (pendingReassigns.length > 0) {
        setReassignModal({ pending: pendingReassigns })
        setIsSaving(false)
        return
      }

      await performSave(sanitized)
    } catch (error) {
      console.error('Failed to save base layout', error)
      errorToast(error instanceof Error ? error.message : 'Failed to save base layout')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReassignUpdate = async () => {
    if (!reassignModal) return

    const sanitized = sanitizeLayoutForSave(layoutDraft)
    setIsSaving(true)
    setReassignModal(null)

    try {
      for (const p of reassignModal.pending) {
        const res = await authFetch(`/base-photos/region/${regionId}/reassign-component`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fromComponentName: p.from, toComponentName: p.to }),
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err?.error ?? 'Failed to reassign photos')
        }
      }
      await performSave(sanitized)
    } catch (error) {
      console.error('Failed to reassign or save', error)
      errorToast(error instanceof Error ? error.message : 'Failed to reassign photos')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReassignLeave = async () => {
    if (!reassignModal) return

    const sanitized = sanitizeLayoutForSave(layoutDraft)
    setIsSaving(true)
    setReassignModal(null)

    try {
      await performSave(sanitized)
    } catch (error) {
      console.error('Failed to save base layout', error)
      errorToast(error instanceof Error ? error.message : 'Failed to save base layout')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={handleAddTab}>Add Tab</Button>
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={!isDirty || isSaving}
          showLoader={false}
        >
          Reset Changes
        </Button>
      </div>

      {layoutDraft.tabs.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No tabs configured yet. Click “Add Tab” to get started.
          </CardContent>
        </Card>
      )}

      {layoutDraft.tabs.map((tab, tabIndex) => {
        const isPermanentArTab = tab.id === BASE_AR_TAB_ID
        const lastMovableIndex = layoutDraft.tabs.reduce(
          (last, item, idx) => (item.id !== BASE_AR_TAB_ID ? idx : last),
          -1,
        )

        return (
        <Card key={tab.id}>
          <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 flex-col gap-2">
              <CardTitle>{isPermanentArTab ? 'AR Tab' : `Tab ${tabIndex + 1}`}</CardTitle>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Label className="md:w-32">Label</Label>
                <Input
                  value={isPermanentArTab ? BASE_AR_TAB_LABEL : tab.label}
                  disabled={isPermanentArTab}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleTabLabelChange(tabIndex, event.target.value)}
                />
              </div>
              {isPermanentArTab && (
                <p className="text-sm text-muted-foreground">
                  Permanent tab visible only to Base Leaders and Admins. Use the same sections as other Hub tabs.
                </p>
              )}
            </div>
            {!isPermanentArTab && (
            <div className="flex gap-2">
              {tabIndex > 0 && (
                <Button
                  variant="outline"
                  onClick={() => handleMoveTab(tabIndex, -1)}
                  showLoader={false}
                >
                  Move Up
                </Button>
              )}
              {tabIndex < lastMovableIndex && (
                <Button
                  variant="outline"
                  onClick={() => handleMoveTab(tabIndex, 1)}
                  showLoader={false}
                >
                  Move Down
                </Button>
              )}
              <Button variant="destructive" onClick={() => handleRemoveTab(tabIndex)}>
                Remove Tab
              </Button>
            </div>
            )}
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => handleAddSection(tabIndex, 'textBox')}>
                Add Text Section
              </Button>
              <Button variant="secondary" onClick={() => handleAddSection(tabIndex, 'dailyMessage')}>
                Add Daily Messages Section
              </Button>
              <Button variant="secondary" onClick={() => handleAddSection(tabIndex, 'basePhotos')}>
                Add Photos Section
              </Button>
              <Button variant="secondary" onClick={() => handleAddSection(tabIndex, 'missionStories')}>
                Add Mission Stories Section
              </Button>
              <Button variant="secondary" onClick={() => handleAddSection(tabIndex, 'prayerRequests')}>
                Add Prayer Requests Section
              </Button>
              <Button variant="secondary" onClick={() => handleAddSection(tabIndex, 'partnerGiveList')}>
                Add Partner Give List Section
              </Button>
            </div>

            {tab.sections.length === 0 && (
              <p className="text-sm text-muted-foreground">No sections yet. Add one to begin configuring this tab.</p>
            )}

            {tab.sections.map((section, sectionIndex) => {
              if (section.type === 'textBox') {
                const isRotating = section.options?.['isRotating'] === true
                const optionDays = (section.options?.['days'] as Record<DayOfWeek, string>) ?? { ...DAILY_MESSAGE_DEFAULT }

                return (
                  <div key={section.id} className="space-y-3 rounded-md border border-border p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Label className="md:w-32">Section Title</Label>
                      <Input
                        value={section.title ?? ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSectionTitleChange(tabIndex, sectionIndex, event.target.value)
                        }
                        placeholder="Optional section title"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`rotating-${section.id}`}
                        checked={isRotating}
                        onCheckedChange={(checked) => handleToggleRotatingTextBox(tabIndex, sectionIndex, checked === true)}
                      />
                      <Label htmlFor={`rotating-${section.id}`} className="text-sm font-normal cursor-pointer">
                        Rotate content daily
                      </Label>
                    </div>

                    {isRotating ? (
                      <>
                        <Separator />
                        <div className="space-y-4">
                          {DAY_ORDER.map((day) => (
                            <div key={`${section.id}-${day}`} className="space-y-2">
                              <Label className="capitalize">{DAY_LABELS[day]}</Label>
                              <RichTextEditor
                                value={optionDays[day] ?? ''}
                                onChange={(value) => handleRotatingTextBoxChange(tabIndex, sectionIndex, day, value)}
                                placeholder={`Content for ${DAY_LABELS[day]}`}
                              />
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <RichTextEditor
                        value={section.content ?? ''}
                        onChange={(value) => handleSectionContentChange(tabIndex, sectionIndex, value)}
                        placeholder="Start typing..."
                      />
                    )}

                    <div className="flex flex-wrap gap-2">
                      {sectionIndex > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, -1)}
                          showLoader={false}
                        >
                          Move Up
                        </Button>
                      )}
                      {sectionIndex < tab.sections.length - 1 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, 1)}
                          showLoader={false}
                        >
                          Move Down
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => handleRemoveSection(tabIndex, sectionIndex)}>
                        Remove Section
                      </Button>
                    </div>
                  </div>
                )
              }

              if (section.type === 'dailyMessage') {
                const optionDays = (section.options?.['days'] as Record<DayOfWeek, string>) ?? { ...DAILY_MESSAGE_DEFAULT }
                const isRotating = section.options?.['isRotating'] === true

                return (
                  <div key={section.id} className="space-y-4 rounded-md border border-border p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Label className="md:w-32">Section Title</Label>
                      <Input
                        value={section.title ?? ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSectionTitleChange(tabIndex, sectionIndex, event.target.value)
                        }
                        placeholder="Optional section title"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`rotating-daily-${section.id}`}
                        checked={isRotating}
                        onCheckedChange={(checked) =>
                          handleSectionOptionChange(tabIndex, sectionIndex, 'isRotating', checked === true)
                        }
                      />
                      <Label htmlFor={`rotating-daily-${section.id}`} className="text-sm font-normal cursor-pointer">
                        Rotate content daily
                      </Label>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      {DAY_ORDER.map((day) => (
                        <div key={`${section.id}-${day}`} className="space-y-2">
                          <Label className="capitalize">{DAY_LABELS[day]}</Label>
                          <RichTextEditor
                            value={optionDays[day] ?? ''}
                            onChange={(value) => handleDailyMessageChange(tabIndex, sectionIndex, day, value)}
                            placeholder={`Message for ${DAY_LABELS[day]}`}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {sectionIndex > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, -1)}
                          showLoader={false}
                        >
                          Move Up
                        </Button>
                      )}
                      {sectionIndex < tab.sections.length - 1 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, 1)}
                          showLoader={false}
                        >
                          Move Down
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => handleRemoveSection(tabIndex, sectionIndex)}>
                        Remove Section
                      </Button>
                    </div>
                  </div>
                )
              }

              if (section.type === 'basePhotos') {
                const limitValue = section.options?.['limit']
                return (
                  <div key={section.id} className="space-y-4 rounded-md border border-border p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Label className="md:w-32">Section Title</Label>
                      <Input
                        value={section.title ?? ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSectionTitleChange(tabIndex, sectionIndex, event.target.value)
                        }
                        placeholder="Optional section title"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This title determines which photos appear here (photos must match this name). Changing it will prompt you to reassign existing photos when you save.
                    </p>

                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Label className="md:w-32">Photo Limit</Label>
                      <Input
                        type="number"
                        min={0}
                        value={typeof limitValue === 'number' ? limitValue : ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const next = event.target.value
                          const parsedValue = next === '' ? null : Number.parseInt(next, 10)
                          handleSectionOptionChange(
                            tabIndex,
                            sectionIndex,
                            'limit',
                            parsedValue !== null && Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined,
                          )
                        }}
                        placeholder="Show all"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Leave blank to display all base photos.</p>

                    <div className="flex flex-wrap gap-2">
                      {sectionIndex > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, -1)}
                          showLoader={false}
                        >
                          Move Up
                        </Button>
                      )}
                      {sectionIndex < tab.sections.length - 1 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, 1)}
                          showLoader={false}
                        >
                          Move Down
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => handleRemoveSection(tabIndex, sectionIndex)}>
                        Remove Section
                      </Button>
                    </div>
                  </div>
                )
              }

              if (section.type === 'missionStories') {
                const useAccordion = section.options?.['useAccordion'] === true
                return (
                  <div key={section.id} className="space-y-4 rounded-md border border-border p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Label className="md:w-32">Section Title</Label>
                      <Input
                        value={section.title ?? ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSectionTitleChange(tabIndex, sectionIndex, event.target.value)
                        }
                        placeholder="Optional section title"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`accordion-${section.id}`}
                        checked={useAccordion}
                        onCheckedChange={(checked) =>
                          handleSectionOptionChange(tabIndex, sectionIndex, 'useAccordion', checked === true)
                        }
                      />
                      <Label htmlFor={`accordion-${section.id}`} className="text-sm font-normal cursor-pointer">
                        Show as accordion (first 4 sentences)
                      </Label>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Mission stories rotate on the page. Content is managed directly from the Base tab.
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {sectionIndex > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, -1)}
                          showLoader={false}
                        >
                          Move Up
                        </Button>
                      )}
                      {sectionIndex < tab.sections.length - 1 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, 1)}
                          showLoader={false}
                        >
                          Move Down
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => handleRemoveSection(tabIndex, sectionIndex)}>
                        Remove Section
                      </Button>
                    </div>
                  </div>
                )
              }

              if (section.type === 'prayerRequests') {
                return (
                  <div key={section.id} className="space-y-4 rounded-md border border-border p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Label className="md:w-32">Section Title</Label>
                      <Input
                        value={section.title ?? ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSectionTitleChange(tabIndex, sectionIndex, event.target.value)
                        }
                        placeholder="Optional section title"
                      />
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Prayer Requests rotate on the page. Content is managed directly from the database and includes Homeowner and Volunteers Prayer Requests.
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {sectionIndex > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, -1)}
                          showLoader={false}
                        >
                          Move Up
                        </Button>
                      )}
                      {sectionIndex < tab.sections.length - 1 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, 1)}
                          showLoader={false}
                        >
                          Move Down
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => handleRemoveSection(tabIndex, sectionIndex)}>
                        Remove Section
                      </Button>
                    </div>
                  </div>
                )
              }

              if (section.type === 'partnerGiveList') {
                return (
                  <div key={section.id} className="space-y-4 rounded-md border border-border p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Label className="md:w-32">Section Title</Label>
                      <Input
                        value={section.title ?? ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSectionTitleChange(tabIndex, sectionIndex, event.target.value)
                        }
                        placeholder="Optional section title"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Description</Label>
                      <RichTextEditor
                        value={section.content ?? ''}
                        onChange={(value) => handleSectionContentChange(tabIndex, sectionIndex, value)}
                        placeholder="Optional description text to display above the partner list"
                      />
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Partner Give List displays partners with giving links for this region. Content is managed directly from the Partners page.
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {sectionIndex > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, -1)}
                          showLoader={false}
                        >
                          Move Up
                        </Button>
                      )}
                      {sectionIndex < tab.sections.length - 1 && (
                        <Button
                          variant="outline"
                          onClick={() => handleMoveSection(tabIndex, sectionIndex, 1)}
                          showLoader={false}
                        >
                          Move Down
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => handleRemoveSection(tabIndex, sectionIndex)}>
                        Remove Section
                      </Button>
                    </div>
                  </div>
                )
              }

              return (
                <div key={section.id} className="space-y-3 rounded-md border border-border p-4">
                  <p className="text-sm text-muted-foreground">
                    The section type <span className="font-semibold">{section.type}</span> is not yet editable. Remove it or contact the development team for support.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {sectionIndex > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => handleMoveSection(tabIndex, sectionIndex, -1)}
                        showLoader={false}
                      >
                        Move Up
                      </Button>
                    )}
                    {sectionIndex < tab.sections.length - 1 && (
                      <Button
                        variant="outline"
                        onClick={() => handleMoveSection(tabIndex, sectionIndex, 1)}
                        showLoader={false}
                      >
                        Move Down
                      </Button>
                    )}
                    <Button variant="destructive" onClick={() => handleRemoveSection(tabIndex, sectionIndex)}>
                      Remove Section
                    </Button>
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>
        )
      })}
      <Dialog open={reassignModal !== null} onOpenChange={(open) => !open && setReassignModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign base photos?</DialogTitle>
            <DialogDescription>
              {reassignModal && reassignModal.pending.length === 1 ? (
                <>
                  {reassignModal.pending[0].count} photo{reassignModal.pending[0].count === 1 ? '' : 's'} {reassignModal.pending[0].count === 1 ? 'is' : 'are'} tagged as &quot;{reassignModal.pending[0].from}&quot;. Changing this section&apos;s title to &quot;{reassignModal.pending[0].to}&quot; will make {reassignModal.pending[0].count === 1 ? 'it' : 'them'} not appear in any section unless you reassign.
                </>
              ) : reassignModal && reassignModal.pending.length > 1 ? (
                <>
                  You changed {reassignModal.pending.length} Base Photos section titles. The following photos would be orphaned:
                  <ul className="mt-2 list-inside list-disc">
                    {reassignModal.pending.map((p, i) => (
                      <li key={i}>&quot;{p.from}&quot; → &quot;{p.to}&quot; ({p.count} photo{p.count === 1 ? '' : 's'})</li>
                    ))}
                  </ul>
                  Update these photos to their new section titles?
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignModal(null)} showLoader={false}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleReassignLeave} showLoader={false}>
              Leave as-is and save
            </Button>
            <Button
              onClick={handleReassignUpdate}
              disabled={isSaving}
              showLoader={isSaving}
            >
              Update {reassignModal?.pending.reduce((s, p) => s + p.count, 0) ?? 0} photo{(reassignModal?.pending.reduce((s, p) => s + p.count, 0) ?? 0) === 1 ? '' : 's'} to new titles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StickySaveBar
        isVisible={isDirty || showSuccessBar}
        isSuccess={showSuccessBar}
        isSubmitting={isSaving}
        onSave={handleSave}
      />
    </div>
  )
}
